const isFilled = value =>
  value !== null &&
  value !== undefined &&
  value !== '-' &&
  String(value).trim() !== '';

const pickNamedValue = value => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') {
    return (
      value.name ??
      value.Name ??
      value.location?.name ??
      value.location?.Name ??
      value.Location?.name ??
      value.Location?.Name ??
      null
    );
  }
  return value;
};

const displayCell = value => {
  const resolved = pickNamedValue(value) ?? value;
  if (!isFilled(resolved)) return '-';
  return String(resolved).trim();
};

const isFobIncoterm = incoterm =>
  String(incoterm || '')
    .trim()
    .toUpperCase() === 'FOB';

const getFobLocationNames = data =>
  (Array.isArray(data?.incotermFOBLocations) ? data.incotermFOBLocations : [])
    .map(location => location?.name ?? location?.Name ?? location)
    .filter(isFilled);

const isFobOriginRoute = (route, names = []) => {
  const routeText = String(route || '')
    .trim()
    .toLowerCase();
  if (routeText === 'origin') return true;
  if (routeText === 'straits') return false;
  return (Array.isArray(names) ? names : []).filter(isFilled).length > 2;
};

const resolveRoute = (row, timelineItem) => {
  const candidates = [
    row?.route,
    timelineItem?.incotermRoute,
    timelineItem?.incotermPortType,
    row?.incotermPortType,
  ];
  for (const candidate of candidates) {
    const resolved = displayCell(candidate);
    if (resolved !== '-') return resolved;
  }
  return '-';
};

// Objek lokasi mentah dipertahankan (bukan hanya namanya) karena alpha HPS
// dyed/undyed jalur non-LPG disimpan per lokasi di QuantityIncotermLocationDetail.
const getFobLocationObjects = data =>
  (Array.isArray(data?.incotermFOBLocations)
    ? data.incotermFOBLocations
    : []
  ).filter(location => location && typeof location === 'object');

// Baris didahulukan atas timeline: object lokasi pada baris berasal dari
// get-delivery-detail yang membawa alphaDyedHps/alphaUndyedHps, sedangkan daftar
// lokasi pada timeline berasal dari /quantity-incoterm yang tidak memuatnya.
// (Urutan ini sengaja berbeda dari collectFobLocationNames, yang hanya butuh nama.)
const collectFobLocations = (row, timelineItem) => {
  const fromRow = getFobLocationObjects(row);
  if (fromRow.length) return fromRow;
  return getFobLocationObjects(timelineItem);
};

const collectFobLocationNames = (row, timelineItem) => {
  const fromTimeline = getFobLocationNames(timelineItem);
  if (fromTimeline.length) return fromTimeline;

  const fromRow = getFobLocationNames(row);
  if (fromRow.length) return fromRow;

  return [
    timelineItem?.incotermLocation,
    timelineItem?.incotermLocation2,
    row?.loadingPort,
    row?.port1,
  ]
    .map(value => displayCell(value))
    .filter(value => value !== '-');
};

const resolveFobLoadingPort = (route, names, fallback) => {
  if (!names.length) return displayCell(fallback);
  if (isFobOriginRoute(route, names)) return names[0];
  return names.join(' or ');
};

const findTimelineItem = (row, timelineItems, index) => {
  if (!timelineItems.length) return null;

  if (row?.quantityIncotermId != null) {
    const matched = timelineItems.find(
      item =>
        item?.quantityIncotermId != null &&
        String(item.quantityIncotermId) === String(row.quantityIncotermId)
    );
    if (matched) return matched;
  }

  return timelineItems[index] ?? null;
};

const mapDisplayRow = (row, timelineItem) => {
  const incoterm = displayCell(row?.incoterm ?? timelineItem?.incoterm);
  const route = resolveRoute(row, timelineItem);
  const fobLocationNames = collectFobLocationNames(row, timelineItem);

  const loadingPort = isFobIncoterm(incoterm)
    ? resolveFobLoadingPort(route, fobLocationNames, row?.loadingPort)
    : '-';

  return {
    ...row,
    incoterm,
    route,
    loadingPort,
    fobLocationNames,
    fobLocations: collectFobLocations(row, timelineItem),
  };
};

const expandFobOriginRows = rows =>
  rows.flatMap(row => {
    if (!isFobIncoterm(row.incoterm)) {
      return [{ ...row, loadingPort: '-' }];
    }

    const names = (row.fobLocationNames ?? []).filter(isFilled);
    if (!isFobOriginRoute(row.route, names) || names.length === 0) {
      return [row];
    }

    return names.map(name => {
      // Dicocokkan lewat nama, bukan indeks: getFobLocationNames membuang lokasi
      // tanpa nama sehingga urutan kedua daftar bisa bergeser.
      const location = (row.fobLocations ?? []).find(
        item => (item?.name ?? item?.Name) === name
      );

      return {
        ...row,
        loadingPort: name,
        alphaDyedHps: location?.alphaDyedHps ?? row.alphaDyedHps ?? null,
        alphaUndyedHps: location?.alphaUndyedHps ?? row.alphaUndyedHps ?? null,
        alphaHps:
          location?.alphaHps ??
          location?.hpsAlpha ??
          row.alphaHps ??
          row.hpsAlpha ??
          null,
      };
    });
  });

export const mapHpsParcelIncotermRows = parcel => {
  const rows = Array.isArray(parcel?.incotermRows) ? parcel.incotermRows : [];
  const timelineItems = Array.isArray(parcel?.incotermTimelineItems)
    ? parcel.incotermTimelineItems
    : [];

  if (!rows.length && timelineItems.length) {
    return expandFobOriginRows(
      timelineItems.map(item => mapDisplayRow({}, item))
    );
  }

  const mapped = rows.map((row, index) =>
    mapDisplayRow(row, findTimelineItem(row, timelineItems, index))
  );

  return expandFobOriginRows(mapped);
};
